import { createRemoteJWKSet, decodeJwt, jwtVerify } from 'jose'
import type { AuthState } from '../types/AuthState'

/**
 * The login session token is delivered to the SPA in the URL fragment
 * (`#login_session_token=...`). It is signed by Keycloak with the realm's RS256
 * key. The browser MUST verify that signature against a TRUSTED, pre-configured
 * issuer before treating the session as authenticated — otherwise anyone who can
 * write a link can hand a victim a forged token on the genuine login origin and
 * redirect the typed password to an attacker-controlled host (via the token's
 * `iss` claim, which is used as the base URL for the discovery/login calls).
 *
 * The set of trusted issuers is injected at container start (see index.html /
 * start.sh -> `window.trustedIssuers`), so it lives outside source code. There
 * are exactly two per environment (the `.mckinsey.id` host and its China
 * `.id.mckinsey.com.cn` twin), e.g.:
 *   https://auth.<env>.mckinsey.id/auth/realms/r
 *   https://auth.<env>.id.mckinsey.com.cn/auth/realms/r
 */

/** Standard Keycloak JWKS path, relative to the realm issuer. */
const CERTS_PATH = '/protocol/openid-connect/certs'

/** Only RS256 is accepted (the realm signing algorithm). Rejects `alg: none`, HS*, etc. */
const ALLOWED_ALGORITHMS = ['RS256']

/** Thrown when a token cannot be trusted (bad signature, untrusted issuer, malformed, ...). */
export class InvalidSessionError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'InvalidSessionError'
  }
}

/** Thrown when a token is otherwise valid (signature + issuer) but has expired. */
export class ExpiredSessionError extends Error {
  public readonly authState?: AuthState

  constructor(message: string, authState?: AuthState) {
    super(message)
    this.name = 'ExpiredSessionError'
    this.authState = authState
  }
}

/**
 * Strip a single trailing slash from a *configured* issuer. Keycloak emits `iss`
 * without a trailing slash (ex: `.../auth/realms/r`), so this only normalizes the
 * `TRUSTED_ISSUERS` config: an accidental `TRUSTED_ISSUERS=.../auth/realms/r/` still
 * matches rather than silently blocking every login. The token's issuer is trusted
 * to be canonical without trailing slash and is compared as-is.
 */
const removeTrailingSlash = (issuer: string): string =>
  issuer.endsWith('/') ? issuer.slice(0, -1) : issuer

const getTrustedIssuers = (): string[] =>
  String(globalThis.trustedIssuers || '')
    .split(',')
    .map((issuer) => removeTrailingSlash(issuer.trim()))
    .filter(Boolean)

type RemoteJWKSet = ReturnType<typeof createRemoteJWKSet>

/**
 * Cache one JWK set per trusted issuer. The URL is always derived from a
 * *trusted* issuer (never from the raw token), which is what prevents an
 * attacker from pointing key discovery at a host they control.
 */
const jwksByIssuer = new Map<string, RemoteJWKSet>()

const getJwks = (issuer: string): RemoteJWKSet => {
  let jwks = jwksByIssuer.get(issuer)
  if (!jwks) {
    jwks = createRemoteJWKSet(new URL(`${issuer}${CERTS_PATH}`))
    jwksByIssuer.set(issuer, jwks)
  }
  return jwks
}

/**
 * Verify a login session token and return its (trusted) claims.
 *
 * @throws {InvalidSessionError} when the token is missing, malformed, signed by
 *   an untrusted key, or carries an issuer that is not explicitly trusted.
 * @throws {ExpiredSessionError} when the token's signature + issuer are valid
 *   but it has expired (so the caller can render the session-timeout screen).
 */
export const verifyLoginSession = async (token: string): Promise<AuthState> => {
  if (!token) throw new InvalidSessionError('Missing login session token')

  const trustedIssuers = getTrustedIssuers()
  if (trustedIssuers.length === 0) {
    throw new InvalidSessionError('No trusted issuers are configured')
  }

  // Read (but do NOT trust) the issuer only to select a *configured* trust
  // anchor. The JWKS is fetched from that trusted issuer, never from an
  // arbitrary issuer supplied by the token.
  let unverifiedIssuer: string | undefined
  try {
    unverifiedIssuer = decodeJwt(token).iss
  } catch {
    throw new InvalidSessionError('Malformed login session token')
  }

  const issuer = trustedIssuers.find((trusted) => trusted === unverifiedIssuer)
  if (!issuer) {
    throw new InvalidSessionError(`Untrusted issuer: ${unverifiedIssuer ?? '<none>'}`)
  }

  try {
    const { payload } = await jwtVerify(token, getJwks(issuer), {
      issuer,
      algorithms: ALLOWED_ALGORITHMS,
    })
    return payload as unknown as AuthState
  } catch (error) {
    const code = (error as { code?: string })?.code
    if (code === 'ERR_JWT_EXPIRED') {
      // Signature + issuer were valid; only the lifetime has lapsed. Surface the
      // (now-trustworthy) claims so the caller can render the timeout screen.
      let authState: AuthState | undefined
      try {
        authState = decodeJwt(token) as unknown as AuthState
      } catch {
        authState = undefined
      }
      throw new ExpiredSessionError('Login session has expired', authState)
    }
    throw new InvalidSessionError((error as Error)?.message || 'Invalid login session token')
  }
}
